import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { 
    UserCog, 
    Plus, 
    Pencil, 
    Trash2, 
    Mail, 
    User,
    Shield,
    Eye,
    EyeOff,
    Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminManagementPage = () => {
    const { admin: currentAdmin } = useAuth();
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: ''
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchAdmins();
    }, []);

    const fetchAdmins = async () => {
        try {
            const response = await axios.get(`${API}/admin/admins`);
            setAdmins(response.data);
        } catch (error) {
            console.error('Error fetching admins:', error);
            toast.error('Failed to load admin accounts');
        } finally {
            setLoading(false);
        }
    };

    const handleAddAdmin = async () => {
        if (!formData.name || !formData.email || !formData.password) {
            toast.error('Please fill in all fields');
            return;
        }

        setSubmitting(true);
        try {
            await axios.post(`${API}/admin/admins`, formData);
            toast.success('Admin account created successfully');
            setIsAddDialogOpen(false);
            setFormData({ name: '', email: '', password: '' });
            fetchAdmins();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create admin');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditAdmin = async () => {
        if (!formData.name || !formData.email) {
            toast.error('Name and email are required');
            return;
        }

        setSubmitting(true);
        try {
            const updateData = {
                name: formData.name,
                email: formData.email
            };
            if (formData.password) {
                updateData.password = formData.password;
            }

            await axios.put(`${API}/admin/admins/${selectedAdmin.id}`, updateData);
            toast.success('Admin account updated successfully');
            setIsEditDialogOpen(false);
            setSelectedAdmin(null);
            setFormData({ name: '', email: '', password: '' });
            fetchAdmins();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update admin');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteAdmin = async () => {
        setSubmitting(true);
        try {
            await axios.delete(`${API}/admin/admins/${selectedAdmin.id}`);
            toast.success('Admin account deleted successfully');
            setIsDeleteDialogOpen(false);
            setSelectedAdmin(null);
            fetchAdmins();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete admin');
        } finally {
            setSubmitting(false);
        }
    };

    const openEditDialog = (admin) => {
        setSelectedAdmin(admin);
        setFormData({
            name: admin.name,
            email: admin.email,
            password: ''
        });
        setIsEditDialogOpen(true);
    };

    const openDeleteDialog = (admin) => {
        setSelectedAdmin(admin);
        setIsDeleteDialogOpen(true);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C9A227]"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-white font-['Playfair_Display']">
                        Admin Management
                    </h1>
                    <p className="text-[#A0A0A0] mt-1">
                        Manage administrator accounts for the portal
                    </p>
                </div>
                <Button
                    onClick={() => {
                        setFormData({ name: '', email: '', password: '' });
                        setIsAddDialogOpen(true);
                    }}
                    className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435]"
                    data-testid="add-admin-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Admin
                </Button>
            </div>

            {/* Admin List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {admins.map((admin) => (
                    <Card 
                        key={admin.id} 
                        className={`bg-[#4A5240] border-[#5A6350] ${
                            currentAdmin?.id === admin.id ? 'ring-2 ring-[#C9A227]' : ''
                        }`}
                        data-testid={`admin-card-${admin.id}`}
                    >
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between mb-4">
                                <div className="w-12 h-12 rounded-full bg-[#C9A227]/20 flex items-center justify-center">
                                    <Shield className="w-6 h-6 text-[#C9A227]" />
                                </div>
                                {currentAdmin?.id === admin.id && (
                                    <span className="text-xs bg-[#C9A227] text-[#3D4435] px-2 py-1 rounded-full font-medium">
                                        You
                                    </span>
                                )}
                            </div>
                            
                            <h3 className="text-white font-semibold text-lg mb-1">{admin.name}</h3>
                            
                            <div className="space-y-2 mb-4">
                                <div className="flex items-center gap-2 text-[#A0A0A0] text-sm">
                                    <Mail className="w-4 h-4" />
                                    <span className="truncate">{admin.email}</span>
                                </div>
                                <div className="flex items-center gap-2 text-[#A0A0A0] text-sm">
                                    <Calendar className="w-4 h-4" />
                                    <span>Added {formatDate(admin.created_at)}</span>
                                </div>
                            </div>

                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openEditDialog(admin)}
                                    className="flex-1 border-[#5A6350] text-[#A0A0A0] hover:bg-[#5A6350] hover:text-white"
                                    data-testid={`edit-admin-${admin.id}`}
                                >
                                    <Pencil className="w-4 h-4 mr-1" />
                                    Edit
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openDeleteDialog(admin)}
                                    disabled={currentAdmin?.id === admin.id}
                                    className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-testid={`delete-admin-${admin.id}`}
                                >
                                    <Trash2 className="w-4 h-4 mr-1" />
                                    Delete
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {admins.length === 0 && (
                <Card className="bg-[#4A5240] border-[#5A6350]">
                    <CardContent className="p-12 text-center">
                        <UserCog className="w-12 h-12 text-[#5A6350] mx-auto mb-4" />
                        <h3 className="text-white font-semibold mb-2">No Admin Accounts</h3>
                        <p className="text-[#A0A0A0]">Create your first admin account to get started.</p>
                    </CardContent>
                </Card>
            )}

            {/* Add Admin Dialog */}
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogContent className="bg-[#4A5240] border-[#5A6350] text-white">
                    <DialogHeader>
                        <DialogTitle className="font-['Playfair_Display'] text-xl">Add New Admin</DialogTitle>
                        <DialogDescription className="text-[#A0A0A0]">
                            Create a new administrator account for the portal.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="text-white">Full Name</Label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Enter full name"
                                    className="pl-10 bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666]"
                                    data-testid="admin-name-input"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-white">Email Address</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                                <Input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="Enter email address"
                                    className="pl-10 bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666]"
                                    data-testid="admin-email-input"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-white">Password</Label>
                            <div className="relative">
                                <Input
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    placeholder="Enter password"
                                    className="pr-10 bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666]"
                                    data-testid="admin-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-white"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsAddDialogOpen(false)}
                            className="border-[#5A6350] text-[#A0A0A0] hover:bg-[#5A6350]"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAddAdmin}
                            disabled={submitting}
                            className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435]"
                            data-testid="confirm-add-admin"
                        >
                            {submitting ? 'Creating...' : 'Create Admin'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Admin Dialog */}
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogContent className="bg-[#4A5240] border-[#5A6350] text-white">
                    <DialogHeader>
                        <DialogTitle className="font-['Playfair_Display'] text-xl">Edit Admin</DialogTitle>
                        <DialogDescription className="text-[#A0A0A0]">
                            Update administrator account details.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="text-white">Full Name</Label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Enter full name"
                                    className="pl-10 bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666]"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-white">Email Address</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                                <Input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="Enter email address"
                                    className="pl-10 bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666]"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-white">New Password (leave blank to keep current)</Label>
                            <div className="relative">
                                <Input
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    placeholder="Enter new password"
                                    className="pr-10 bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666]"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-white"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsEditDialogOpen(false)}
                            className="border-[#5A6350] text-[#A0A0A0] hover:bg-[#5A6350]"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleEditAdmin}
                            disabled={submitting}
                            className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435]"
                        >
                            {submitting ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent className="bg-[#4A5240] border-[#5A6350] text-white">
                    <DialogHeader>
                        <DialogTitle className="font-['Playfair_Display'] text-xl text-red-400">Delete Admin</DialogTitle>
                        <DialogDescription className="text-[#A0A0A0]">
                            Are you sure you want to delete this administrator account? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedAdmin && (
                        <div className="py-4">
                            <div className="bg-[#3D4435] rounded-lg p-4">
                                <p className="text-white font-semibold">{selectedAdmin.name}</p>
                                <p className="text-[#A0A0A0] text-sm">{selectedAdmin.email}</p>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsDeleteDialogOpen(false)}
                            className="border-[#5A6350] text-[#A0A0A0] hover:bg-[#5A6350]"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDeleteAdmin}
                            disabled={submitting}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="confirm-delete-admin"
                        >
                            {submitting ? 'Deleting...' : 'Delete Admin'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminManagementPage;
